import { useEffect, useState } from 'react';

import { applySchemaValidators } from './validators';
import { applyDescriptionModifiers } from './descriptionModifiers';
import { getSchemaFromUserType, extractTaskNamesFromSchema } from './constants';

import buildLaunchpadFromSchema from 'utils/buildLaunchpadFromSchema';
import useOperatorTasks, { UseOperatorTasksOptions } from 'services/operatorTasks/queries/useOperatorTasks';
import UserType from 'modules/common/types/UserType.enum';
import { useMember } from 'modules/common/queries/member';
import { LaunchpadItem, TaskStatus } from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';
import useCheckGate from 'modules/common/useCheckGate';

export default function useLaunchpadSchema(options?: UseOperatorTasksOptions) {
    const checkGate = useCheckGate();
    // Refetch in case member fields like state_of_inc are updated in Hub
    const { data: member, isFetching: isFetchingMember } = useMember({ refetchOnMount: 'always' });

    // State_of_inc can be null before it's set, so fall back to the business address
    const formationState = member?.business?.state_of_inc?.id || member?.business_address?.state;

    // Build base schema (includes all tasks - filtering happens via validators)
    const baseSchema = member?.usertype ? getSchemaFromUserType(member.usertype as UserType, formationState) : [];

    // Extract task names from base schema to fetch all tasks data
    // Base schema is a superset of filtered schema, so we fetch once and use for both validation and building items
    const baseTaskNames = extractTaskNamesFromSchema(baseSchema);
    const baseTaskNamesKey = baseTaskNames.join('|');

    // Latch completion per task set so added tasks aren't left disabled by prior completion.
    const [completedTaskNamesKey, setCompletedTaskNamesKey] = useState<string | null>(null);
    const allTasksAlreadyCompleted = completedTaskNamesKey === baseTaskNamesKey;

    // Fetch tasks data for validation and building launchpad items
    const refetchInterval = options?.refetchInterval ?? 60000; // 60 seconds by default
    const {
        data: tasksData,
        isPending: isPendingLaunchpadTasks,
        refetch,
    } = useOperatorTasks(baseTaskNames, {
        refetchOnMount: false,
        staleTime: Infinity,
        ...options,
        // Gate on the member refetch so stale cached data can't evaluate the wrong task set
        enabled: Boolean(member?.id) && !isFetchingMember && !allTasksAlreadyCompleted && (options?.enabled ?? true),
        refetchInterval: allTasksAlreadyCompleted ? false : refetchInterval,
        skipHardcodedStatus: true,
    });

    // Apply validators to filter tasks/subtasks based on their data and member context
    const launchpadTasksSchema = applySchemaValidators(baseSchema, tasksData || {}, member, checkGate);

    // Build launchpad items from filtered schema using fetched task data
    const builtLaunchpadItems =
        tasksData && !isPendingLaunchpadTasks
            ? buildLaunchpadFromSchema(launchpadTasksSchema, tasksData as Record<string, LaunchpadItem>)
            : [];

    // Apply description modifiers to update descriptions dynamically
    const launchpadItems = applyDescriptionModifiers(builtLaunchpadItems, tasksData || {}, member);

    const allTasksCompleted = launchpadItems.every(
        (item) => item.allSubtasksCompleted || item.status === TaskStatus.COMPLETED
    );

    useEffect(() => {
        if (!isFetchingMember && launchpadTasksSchema.length > 0 && !isPendingLaunchpadTasks && allTasksCompleted) {
            setCompletedTaskNamesKey(baseTaskNamesKey);
        }
    }, [isFetchingMember, isPendingLaunchpadTasks, launchpadTasksSchema.length, allTasksCompleted, baseTaskNamesKey]);

    return {
        launchpadTasks: tasksData,
        refetchLaunchpadTasks: refetch,
        launchpadItems,
        isPendingLaunchpadTasks,
        allTasksCompleted,
    };
}
